import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { ChartCard } from './ChartCard'
import type { ConnectionsLatest, SeriesPoint } from '../../types/monitoring'

export function ConnectionsPanel({ latest, series, loading, error }: {
  latest: ConnectionsLatest | undefined; series: SeriesPoint[]; loading: boolean; error?: string
}) {
  const cards = [
    { label: 'TCP Established', value: latest?.tcp_established },
    { label: 'TCP Listen', value: latest?.tcp_listen },
    { label: 'TCP Time-Wait', value: latest?.tcp_time_wait },
    { label: 'TCP Close-Wait', value: latest?.tcp_close_wait },
    { label: 'UDP Total', value: latest?.udp_total },
  ]

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <ChartCard title="TCP/UDP Connections" subtitle="Connection counts by state over time" loading={loading} error={error} isEmpty={!series.length}>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={series} margin={{ left: -20, right: 8 }}>
              <defs>
                <linearGradient id="tcpEstGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0284c7" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#0284c7" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="#f0f0f0" />
              <XAxis dataKey="time" tick={{ fontSize: 10, fill: '#a3a3a3' }} axisLine={false} tickLine={false}
                tickFormatter={(t: string) => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} />
              <YAxis tick={{ fontSize: 11, fill: '#a3a3a3' }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip labelFormatter={(l) => new Date(l).toLocaleString()} />
              <Area type="monotone" dataKey="tcp_established" name="Established" stroke="#0284c7" strokeWidth={2} fill="url(#tcpEstGradient)" isAnimationActive={false} />
              <Area type="monotone" dataKey="tcp_time_wait" name="Time-Wait" stroke="#f59e0b" strokeWidth={2} fillOpacity={0} isAnimationActive={false} />
              <Area type="monotone" dataKey="udp_total" name="UDP" stroke="#7c3aed" strokeWidth={2} fillOpacity={0} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-neutral-200 p-6">
        <h2 className="font-bold text-secondary mb-4">Connection States</h2>
        {loading ? (
          <div className="h-56 bg-neutral-100 rounded-lg animate-pulse" />
        ) : (
          <div className="space-y-3 text-sm">
            {cards.map((c) => (
              <div key={c.label} className="flex items-center justify-between py-1.5 border-b border-neutral-50 last:border-0">
                <span className="text-neutral-500">{c.label}</span>
                <span className="font-semibold text-neutral-800 tabular-nums">{c.value?.toLocaleString() ?? '—'}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
